<template>
  <!--
    Locale layout (spec 045-multilingual-support, Phase 7 / WE-1 + WE-3).

    The parent of every locale-prefixed route (`/:locale/...`). It owns no
    chrome of its own — the app-wide Header / Footer live in App.vue — it
    exists to provide the nested `<router-view>` that renders the real routed
    views (MainFlow, Blog, Contact, …) underneath the `/:locale` segment, AND
    to emit the per-locale SEO head (canonical + full hreflang alternates) that
    every prefixed page shares (WE-3 / FR-015). The active locale is set +
    persisted by the router `beforeEach` guard, not here.
  -->
  <RouterView />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import { supportedLocales, type SupportedLocale } from '@/i18n';
import { localePath, stripLocalePrefix } from '@/router/localePath';

const route = useRoute();
const { locale } = useI18n();

// Canonical / x-default locale.
const DEFAULT_LOCALE: SupportedLocale = 'en';

// Absolute-URL origin. Read live in the browser; empty during any non-browser
// render (the static prerenderer writes its OWN <head>, so it stays
// authoritative and we never emit a half-formed canonical there).
const origin =
  typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';

/**
 * Per-locale canonical + full hreflang alternates (FR-015).
 *
 * EVERY prefix-swappable page (home, blog index, contact, data-and-privacy)
 * shares one URL shape that differs only by the `/:locale` segment, so a single
 * head declaration here covers them all: strip the active prefix to the locale
 * -independent "logical" path, then rebuild it for the current locale (canonical)
 * and for all 17 locales (alternates) via the same `localePath` helper the links
 * use — keeping runtime URLs, the sitemap and the prerenderer in lock-step.
 *
 * Blog POSTS are the one exception: their in-path `<lang>`/`<slug>` differ per
 * locale (not a pure prefix swap), so `BlogPost.vue` owns its own canonical and
 * we skip it here to avoid emitting a wrong alternate set / a duplicate tag.
 */
const seo = computed(() => {
  if (route.name === 'blog-post') return null;
  // The path WITHOUT the locale prefix, e.g. `/contact`, `/blog`, `/` (home).
  const logicalPath = stripLocalePrefix(route.path);
  const canonical = origin + localePath(logicalPath, locale.value as SupportedLocale);
  const alternates: { rel: string; hreflang: string; href: string }[] = supportedLocales.map((loc) => ({
    rel: 'alternate',
    hreflang: loc,
    href: origin + localePath(logicalPath, loc),
  }));
  alternates.push({
    rel: 'alternate',
    hreflang: 'x-default',
    href: origin + localePath(logicalPath, DEFAULT_LOCALE),
  });
  return { canonical, alternates };
});

useHead(() => {
  const s = seo.value;
  if (!s) return {};
  return {
    link: [{ rel: 'canonical', href: s.canonical }, ...s.alternates],
    meta: [{ property: 'og:url', content: s.canonical }],
  };
});
</script>
